import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import merchantService from '../services/merchantService';
import { useAppDialog } from '../components/AppDialog';
import FloatingBannerStack from '../components/FloatingBannerStack';
import Pagination from '../components/Pagination';
import { SearchIcon, EditIcon, TrashIcon } from '../components/Icons';
import { MERCHANT_UPDATED_EVENT } from '../utils/merchantEvents';

// Simple Eye Icon for View
const EyeIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const Merchants = () => {
  const navigate = useNavigate();
  const appDialog = useAppDialog();
  const { admin, hasPermission } = useAuth();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deletingMerchantId, setDeletingMerchantId] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const searchRequest = useRef(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  const fetchMerchants = useCallback(async (page, search, limit) => {
    const request = ++searchRequest.current;
    try {
      setLoading(true);
      setError(null);
      const res = await merchantService.getMerchants({ page, limit, search });
      if (request !== searchRequest.current) return;
      if (res.success) {
        setMerchants(res.data);
        setPagination(prev => ({ 
          ...prev, 
          page: res.pagination.page, 
          total: res.pagination.total, 
          pages: res.pagination.pages 
        }));
      } else {
        setError(res.message || 'An error occurred while fetching merchants');
      }
    } catch (err) {
      if (request === searchRequest.current) setError('An error occurred while fetching merchants');
    } finally {
      if (request === searchRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchMerchants(1, searchTerm, pagination.limit), 400);
    return () => {
      clearTimeout(t);
      searchRequest.current += 1;
    };
  }, [searchTerm, pagination.limit, fetchMerchants]);

  useEffect(() => {
    const refreshMerchants = () => fetchMerchants(pagination.page, searchTerm, pagination.limit);
    window.addEventListener(MERCHANT_UPDATED_EVENT, refreshMerchants);
    return () => window.removeEventListener(MERCHANT_UPDATED_EVENT, refreshMerchants);
  }, [fetchMerchants, pagination.page, pagination.limit, searchTerm]);

  const handleDeleteMerchant = async (merchant) => {
    const confirmed = await appDialog.confirm({
      title: 'Delete merchant',
      message: 'Delete this merchant permanently?',
      confirmLabel: 'Delete',
      variant: 'danger',
      details: [
        { label: 'Name', value: merchant.name },
        { label: 'Merchant code', value: merchant.merchant_code }
      ],
      note: 'This action cannot be undone.'
    });
    if (!confirmed) return;

    setDeletingMerchantId(merchant._id);
    setActionError('');
    const res = await merchantService.deleteMerchant(merchant._id);
    setDeletingMerchantId('');

    if (res.success) {
      setSuccessMessage(`Merchant ${merchant.name} deleted`);
      const nextPage = merchants.length === 1 && pagination.page > 1
        ? pagination.page - 1
        : pagination.page;
      fetchMerchants(nextPage, searchTerm, pagination.limit);
    } else {
      setActionError(res.message || 'Failed to delete merchant');
    }
  };

  const canView = hasPermission('merchants', 'view');
  const canEdit = hasPermission('merchants', 'edit');
  const canDelete = admin?.role === 'super_admin';
  const showActions = canView || canEdit || canDelete;
  const isSearching = Boolean(searchTerm.trim());
  const tableHeaders = ['Name', 'Merchant Code', 'Client Code', 'Providers', isSearching ? 'Matches' : null, 'Status', showActions ? 'Actions' : null].filter(Boolean);

  return (
    <div className="merchants-page">
      <FloatingBannerStack
        error={actionError}
        success={successMessage}
        onDismissError={() => setActionError('')}
        onDismissSuccess={() => setSuccessMessage('')}
      />
      {hasPermission('merchants', 'create') && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={() => navigate('/merchants/new')}>Add Merchant</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div className="search-container">
          <div className="search-icon-wrapper"><SearchIcon /></div>
          <input 
            type="search"
            className="search-input" 
            aria-label="Search across all merchants"
            aria-describedby="merchant-search-help"
            placeholder="Search merchants, providers, payment gateways, URLs, IPs..."
            value={searchTerm} 
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setLoading(true);
              setError(null);
            }}
          />
        </div>
        <p id="merchant-search-help" style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Search all merchants and their assigned products, sources, categories, countries, currencies, URLs, IPs, and group links. For example, search Kingmidas to find merchants using it.
        </p>
      </div>

      {loading && merchants.length === 0 ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading merchants...</div>
      ) : error ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table-striped" aria-busy={loading} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                {tableHeaders.map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={tableHeaders.length} role="status" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading merchants...</td></tr>
              ) : merchants.length === 0 ? (
                <tr><td colSpan={tableHeaders.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No merchants found.</td></tr>
              ) : merchants.map((m) => (
                <tr key={m._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 600 }}>{m.name}</td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}><code style={{ backgroundColor: 'var(--bg-soft)', padding: '0.2rem 0.4rem', borderRadius: '4px' }}>{m.merchant_code}</code></td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>
                    {m.client_code ? (
                      <code style={{ backgroundColor: 'var(--bg-soft)', padding: '0.2rem 0.4rem', borderRadius: '4px' }}>{m.client_code}</code>
                    ) : (
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {m.providers?.length || 0} providers
                  </td>
                  {isSearching && (
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', maxWidth: '360px', overflowWrap: 'anywhere' }}>
                      {(m.search_matches || []).map((match, index) => (
                        <div key={index} style={{ marginBottom: '0.35rem' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>{match.label}: </span>{match.value}
                        </div>
                      ))}
                    </td>
                  )}
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
                    <span style={{ 
                        padding: '0.25rem 0.625rem', 
                        borderRadius: '9999px', fontSize: '0.75rem', 
                        fontWeight: 600, 
                        backgroundColor: m.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                        color: m.is_active ? 'var(--success)' : 'var(--error)' 
                    }}>{m.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                   {showActions && (
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      {canView && (
                        <button className="btn btn-secondary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }} onClick={() => navigate(`/merchants/${m._id}/view`)}>
                          <EyeIcon size={14} />
                          View
                        </button>
                      )}
                      {canEdit && (
                        <button className="btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }} onClick={() => navigate(`/merchants/${m._id}`)}>
                          <EditIcon size={14} />
                          Edit
                        </button>
                      )}
                      {canDelete && (
                        <button className="btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '0.3rem' }} onClick={() => handleDeleteMerchant(m)} disabled={Boolean(deletingMerchantId)}>
                          <TrashIcon size={14} />
                          {deletingMerchantId === m._id ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </td>
                   )}
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination pagination={pagination} onPageChange={(pg) => fetchMerchants(pg, searchTerm, pagination.limit)} onLimitChange={(l) => {
            setPagination(prev => ({ ...prev, limit: l }));
          }} loading={loading} itemName="merchants" />
        </div>
      )}
    </div>
  );
};

export default Merchants;
